/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  CircularProgress,
  FormHelperText,
  TextField,
} from "@mui/material";
import { usePrevious } from "common/hooks/usePrevious";
import _, { find, get, orderBy } from "lodash";
import { Option } from "modules/UAA/configs/constant";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

const CoreAutocomplete = (props) => {
  const {
    className,
    name,
    options,
    label,
    hiddenlabel,
    placeholder,
    InputLabelProps,
    inputProps,
    InputProps,
    shrink,
    required,
    readOnly,
    valuePath,
    labelPath,
    loading,
    returnValueType,
    multiple,
    disabled,
    helperText,
    isCreateable,
    AutoCompleteClassName,
    rules,
    defaultValue,
    formFilter,
    dependFields,
    size = "medium",
    title,
    dependFieldsTrigger,
    filterOptions,
    freeSolo = false,
    isChangeData = false,
    type = "",
    hidden = false,
    limitTags = undefined,
    ...restProps
  } = props;

  const InputLabelPropsDefault = _.isNil(InputLabelProps)
    ? { shrink: true, required }
    : InputLabelProps;

  const { t } = useTranslation("common");

  const { control, setValue } = useFormContext();
  const watchName = useWatch({ control, name });
  const preLoading = usePrevious(loading);

  useEffect(() => {
    //check selected khi load xong option
    if (preLoading && !loading) {
      let union;
      if (!_.isNil(options) && !_.isEmpty(options)) {
        if (multiple) {
          if (returnValueType === "option") {
            union = _.unionWith(options, watchName, _.isEqual);
          } else {
            union = _.unionBy(options, watchName, valuePath);
          }
        } else {
          if (returnValueType === "option") {
            union = _.find(options, _.negate(_.isEqual));
          } else {
            // debugger;
            union = _.find(
              options,
              ({ value }) =>
                !_.isEqual(value, Option.ALL) && _.eq(value, watchName)
            );
          }
        }
      }

      if (_.isNil(union) && !!watchName) {
        setValue(name, defaultValue || null);
        // trigger(name);
      }
    }
  }, [loading]);

  if (hidden) {
    return null;
  }

  return (
    <div className={className}>
      <Controller
        control={control}
        name={name}
        rules={{
          ...rules,
          validate: {
            required: (v) => {
              if (required && _.isNil(v)) {
                return "message.emptyField";
              }
            },
          },
          deps: dependFieldsTrigger,
        }}
        render={(prop) => {
          const {
            field: { onChange, value, ref },
            fieldState: { error },
          } = prop;

          const valueOption = getValueOption(
            value,
            options,
            multiple,
            returnValueType
          );
          const { message: messageError } = error || {};
          const isError = !!messageError;
          return (
            <Autocomplete
              freeSolo={freeSolo}
              className={AutoCompleteClassName}
              multiple={multiple}
              limitTags={limitTags}
              // ListboxComponent={ListCombo}
              filterSelectedOptions={true}
              filterOptions={filterOptions}
              getOptionLabel={(option) => _.get(option, labelPath) ?? ""}
              loading={loading}
              options={options}
              readOnly={readOnly || disabled}
              noOptionsText={t("form.autocomplete.no_options")}
              disabled={disabled}
              value={valueOption}
              onChange={(_, value) => {
                const outValueEnum = get(value, valuePath) || null;

                if (type === "idType") {
                  setValue("idNumber", undefined);
                }

                return returnValueType === "enum"
                  ? onChange(
                      multiple
                        ? value.map((v) => get(v, valuePath)) || []
                        : !outValueEnum && !!defaultValue
                        ? defaultValue
                        : outValueEnum
                    )
                  : onChange(value);
              }}
              renderOption={(props, option) => {
                const { id } = props;
                return (
                  <li {...props} key={id}>
                    {get(option, labelPath)}
                  </li>
                );
              }}
              renderInput={(params) => {
                return (
                  <>
                    <TextField
                      inputRef={ref}
                      {...params}
                      placeholder={
                        placeholder ??
                        t("form.autocomplete.placeholder", { label })
                      }
                      sx={
                        isChangeData
                          ? {
                              "& .MuiInputBase-root.Mui-disabled": {
                                "& > fieldset": {
                                  borderColor: "#F89E19",
                                },
                              },
                            }
                          : {}
                      }
                      label={!hiddenlabel ? label : ""}
                      error={isError}
                      helperText={t(messageError, { field: title ?? label })}
                      InputLabelProps={{
                        ...InputLabelPropsDefault,
                        style: isChangeData ? { color: "#F89E19" } : null,
                      }}
                      inputProps={{
                        ...params.inputProps,
                        readOnly,
                        ...inputProps,
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                        ...InputProps,
                      }}
                      size={size}
                    />
                    {helperText && (
                      <FormHelperText>{helperText}</FormHelperText>
                    )}
                  </>
                );
              }}
              {..._.omit(restProps, IGNORE)}
            />
          );
        }}
      />
    </div>
  );
};

const IGNORE = ["cacheKey", "isCacheKey"];

CoreAutocomplete.defaultProps = {
  className: null,
  options: [],
  label: null,
  hiddenlabel: false,
  placeholder: null,
  InputLabelProps: null,
  inputProps: null,
  InputProps: null,
  required: false,
  readOnly: false,
  loading: false,
  filter: undefined,
  valuePath: "value",
  labelPath: "label",
  returnValueType: "enum",
  isCacheKey: true,
  cacheKey: undefined,
  isCreateable: false,
  dependFields: [],
  dependFieldsTrigger: [],
  autoFocus: false,
};

CoreAutocomplete.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  label: PropTypes.string,
  title: PropTypes.string,
  hiddenlabel: PropTypes.bool,
  placeholder: PropTypes.string,
  InputLabelProps: PropTypes.object,
  inputProps: PropTypes.object,
  InputProps: PropTypes.object,
  formFilter: PropTypes.bool,
  loading: PropTypes.bool,
  required: PropTypes.bool,
  readOnly: PropTypes.bool,
  disableClearable: PropTypes.bool,
  disabled: PropTypes.bool,
  valuePath: PropTypes.string,
  labelPath: PropTypes.string,
  returnValueType: PropTypes.oneOf(["option", "enum"]),
  multiple: PropTypes.bool,
  isCacheKey: PropTypes.bool,
  cacheKey: PropTypes.string,
  helperText: PropTypes.any,
  filterOptions: PropTypes.func,
  getOptionLabel: PropTypes.func,
  isCreateable: PropTypes.bool,
  AutoCompleteClassName: PropTypes.string,
  rules: PropTypes.object,
  defaultValue: PropTypes.any,
  dependFields: PropTypes.array,
  dependFieldsTrigger: PropTypes.array,
  autoFocus: PropTypes.bool,
  freeSolo: PropTypes.bool,
  isChangeData: PropTypes.bool,
  type: PropTypes.string,
  hidden: PropTypes.bool,
  limitTags: PropTypes.number,
  size: PropTypes.string,
};

export default React.memo(CoreAutocomplete);

const getValueOption = (
  value,
  options,
  multiple = false,
  returnValueType = "enum",
  type
) => {
  if (returnValueType === "enum") {
    if (multiple) {
      // return (
      //   filter(options, (item) => _.includes(value, _.get(item, "value"))) || []
      // );

      /**
       * using Lodash's _.filter() function to filter the options array
       * based on the values present in the value array.
       * Then, we use _.orderBy() to sort the filtered array
       * based on the order of values in the value array using the value.indexOf(option.value)
       * as the sorting criterion.
       */

      return orderBy(
        _.filter(options, (option) => value?.includes(option.value)),
        (option) => value.indexOf(option.value)
      );
    }
    const valueSelectFormOption =
      find(options, (item) => _.eq(_.get(item, "value"), value)) ?? null;
    return valueSelectFormOption;
  } else {
    const valueSelectFormOption =
      find(options, (item) => _.isEqual(item, value)) ?? null;
    return valueSelectFormOption;
  }
};

// const ListCombo = ({ children }) => {
//   const itemData = [];
//   children.forEach((item) => {
//     itemData.push(item);
//     itemData.push(...(item.children || []));
//   });

//   console.log("itemData: ", itemData);
//   return <>123</>;
// };
